import './assets/main.scss';

import '@/helpers/firebase';

import { createApp } from 'vue';

import { createPinia } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';

import App from './App.vue';
import router from './router';

import { tag } from '@shared/helpers/tag';
import gtagPlugin from '@shared/plugins/gtag';
import mixpanel from '@shared/plugins/mixpanel';
import { i18n } from './plugins/i18n';
import { vuetify } from './plugins/vuetify.treeshaken';

const app = createApp(App);

app
  .use(createPinia().use(piniaPluginPersistedstate))
  .use(router)
  .use(vuetify)
  .use(i18n)
  .use(gtagPlugin, {
    gtagId: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID,
  })
  .use(mixpanel, {
    token: import.meta.env.VITE_MIXPANEL_TOKEN as string,
    config: {
      debug: false,
      ignore_dnt: false,
    },
  });

async function setupSentry() {
  const { browserTracingIntegration, init, replayIntegration } = await import('@sentry/vue');

  init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [browserTracingIntegration({ router }), replayIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // We recommend adjusting this value in production
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [
      // 'localhost',
      /^https:\/\/training-portal-development\.web\.app\//,
    ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_ENV_NAME,
  });
}

// Track page visits
router.afterEach((to) => {
  if (window.location.hostname === 'localhost') return;

  // Track visits to ekiden.ai and athlete.ekiden.ai
  const hostname = window.location.hostname;
  if (hostname === 'ekiden.ai' || hostname === 'www.ekiden.ai') {
    tag('Visit ekiden.ai', {
      page: to.path,
      referrer: document.referrer,
    });
  } else if (hostname === 'athlete.ekiden.ai') {
    tag('Visit athlete.ekiden.ai', {
      page: to.path,
      referrer: document.referrer,
    });
  }
});

if (window.location.hostname !== 'localhost') {
  void setupSentry();
}

app.mount('#app');
